import { useMemo } from "react";
import { Alert, Stack, Table, Text } from "@mantine/core";
import type { LegacySourceComparison } from "../../../types/legacyCases";

interface Props {
  comparison?: LegacySourceComparison;
  hasRowConflicts: boolean;
}

export default function LegacySourceDiscrepancies({
  comparison,
  hasRowConflicts,
}: Props) {
  const differences = useMemo(
    () =>
      comparison?.fields.filter(
        (field) => field.status === "conflict" || field.status === "unverified"
      ) || [],
    [comparison]
  );
  if (!hasRowConflicts && !differences.length) return null;
  return (
    <Alert
      color="orange"
      title="Source values need review"
      role="alert"
      variant="light"
      styles={{ body: { minWidth: 0 }, message: { minWidth: 0 } }}
    >
      <Stack gap="sm" miw={0}>
        <Text size="sm">
          Compare these original values, correct the patient and dates below,
          then acknowledge the differences before importing. The final report
          supplies the clinical narrative. Patient records are never updated
          automatically.
        </Text>
        {hasRowConflicts && (
          <Text size="sm">
            Spreadsheet rows also differ. Review the selected source row.
          </Text>
        )}
        {differences.length > 0 && (
          <Table.ScrollContainer minWidth={400}>
            <Table verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Field</Table.Th>
                  <Table.Th>Spreadsheet</Table.Th>
                  <Table.Th>Final report</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {differences.map((field) => (
                  <Table.Tr key={field.field}>
                    <Table.Td>
                      <Text size="sm" fw={600}>
                        {field.label}
                      </Text>
                      <Text size="xs">
                        {field.status === "unverified"
                          ? "Verify format in PDF"
                          : "Values differ"}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" style={{ overflowWrap: "anywhere" }}>
                        {field.spreadsheetValue || "Not provided"}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {field.reportValues.map((value, index) => (
                        <Text
                          size="sm"
                          key={index}
                          style={{
                            whiteSpace: "pre-wrap",
                            overflowWrap: "anywhere",
                          }}
                        >
                          {value.rawValue}
                          {value.spans.length > 0 &&
                            ` (page ${Array.from(
                              new Set(
                                value.spans.map((span) => span.pageNumber)
                              )
                            ).join(", ")})`}
                        </Text>
                      ))}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Stack>
    </Alert>
  );
}
